import apiClient from './client';

export interface DashboardStats {
  total_members: number;
  active_members: number;
  inactive_members: number;
  /** Size of the pastoral follow-up list (unbaptized adults + prodigals). */
  outreach_count: number;
  total_families: number;
  total_groups: number;
  total_visits: number;
  new_members_this_month: number;
  new_members_last_month: number;
  members_by_status: Record<string, number>;
  members_by_type: Record<string, number>;
  members_by_sector: { sector_name: string; count: number }[];
  gender_distribution: Record<string, number>;
  age_distribution: { range: string; count: number }[];
}

export interface MonthlyTrend {
  month: string;
  new_members: number;
  total_members: number;
  visits: number;
}

export interface Anniversary {
  id: string;
  full_name: string;
  photo_url: string | null;
  date: string;
  type: 'birthday' | 'membership' | 'wedding';
  years: number | null;
}

export interface RecentActivity {
  id: string;
  action: string;
  description: string;
  created_at: string;
  user_name: string;
}

export async function getStats(): Promise<DashboardStats> {
  const response = await apiClient.get<DashboardStats>('/dashboard/stats/');
  return response.data;
}

export async function getAnniversaries(
  params?: { days_ahead?: number; type?: string },
): Promise<Anniversary[]> {
  const response = await apiClient.get<Anniversary[]>(
    '/dashboard/anniversaries/',
    { params },
  );
  return response.data;
}

export async function getRecentActivity(): Promise<RecentActivity[]> {
  const response = await apiClient.get<RecentActivity[]>(
    '/dashboard/recent-activity/',
  );
  return response.data;
}

export async function getReportTrends(): Promise<MonthlyTrend[]> {
  const response = await apiClient.get<MonthlyTrend[]>(
    '/dashboard/report-trends/',
  );
  return response.data;
}

export interface GdprMember {
  id: string;
  full_name: string;
  sector_name: string | null;
}

export interface GdprStats {
  total_members: number;
  signed: number;
  unsigned: number;
  signed_pct: number;
  unsigned_members: GdprMember[];
  signed_members: GdprMember[];
}

export async function getGdprStats(): Promise<GdprStats> {
  const response = await apiClient.get<GdprStats>('/dashboard/gdpr-stats/');
  return response.data;
}

export async function exportGdprList(list: 'unsigned' | 'signed' = 'unsigned'): Promise<Blob> {
  const response = await apiClient.get('/dashboard/gdpr-stats/export', {
    params: { list },
    responseType: 'blob',
  });
  return response.data as Blob;
}

/* ── Registry Stats ── */
export interface RegistryMember {
  id: string;
  full_name: string;
  sector_name: string | null;
  status_date: string | null;
  reason: string | null;
  notes: string | null;
  transfer_church: string | null;
  transfer_city: string | null;
  death_date: string | null;
  church_status?: string;
  /** Real status for transferred group entries: transferred | left | excommunicated. */
  origin_status?: string;
}

export interface RegistryCategory {
  count: number;
  members: RegistryMember[];
}

export interface RegistryStats {
  // "transferred" now includes left + excommunicated (folded per pastoral request).
  deceased: RegistryCategory;
  transferred: RegistryCategory;
  prodigal: RegistryCategory;
}

export async function getRegistryStats(): Promise<RegistryStats> {
  const response = await apiClient.get<RegistryStats>('/dashboard/registry-stats/');
  return response.data;
}

/* ── Member Breakdowns ── */
export interface AgeRange {
  range: string;
  count: number;
}

export interface BaptismOrigin {
  here: number;
  elsewhere: number;
  unspecified: number;
  home_keyword: string;
}

export interface BaptismPlace {
  place: string;
  count: number;
}

export interface MemberBreakdowns {
  gender_members: Record<string, number>;
  gender_dependents: Record<string, number>;
  age_dependents: AgeRange[];
  age_members: AgeRange[];
  civil_status: Record<string, number>;
  baptism_origin: BaptismOrigin;
  baptism_places: BaptismPlace[];
}

export interface AgeRangeBucket {
  min: number;
  max: number;
}

export async function getMemberBreakdowns(params?: {
  age_ranges_members?: string;
  age_ranges_dependents?: string;
}): Promise<MemberBreakdowns> {
  const response = await apiClient.get<MemberBreakdowns>('/dashboard/member-breakdowns/', { params });
  return response.data;
}
